import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { site, telLink } from "@/config/site";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Industries", href: "#industries" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Process", href: "#process" },
  { label: "FAQs", href: "#faqs" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-300 ${
        solid
          ? "bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_20px_rgba(10,31,68,0.06)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-8">
        <a href="#home" data-testid="navbar-logo" className="flex items-center" aria-label="BNZ Solutions home">
          <img
            src={solid ? "/logo.png" : "/logo-white.png"}
            alt="BNZ Solutions — Office Branding, Signage & Printing"
            className="h-10 md:h-12 w-auto transition-opacity duration-300"
            width="192"
            height="105"
          />
        </a>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`navbar-link-${l.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors duration-200 hover:text-gold ${
                solid ? "text-charcoal" : "text-white/90"
              }`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={telLink}
            data-testid="navbar-call"
            className={`hidden md:flex items-center gap-2 text-sm font-medium transition-colors hover:text-gold ${
              solid ? "text-navy" : "text-white"
            }`}
          >
            <Phone className="h-4 w-4" /> {site.phone}
          </a>
          <a
            href="#contact"
            data-testid="navbar-cta-quote"
            className="hidden sm:inline-flex items-center bg-gold px-5 py-2.5 text-sm font-semibold text-navy transition-colors duration-200 hover:bg-gold-dark hover:text-white"
          >
            Get a Quote
          </a>

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="navbar-mobile-trigger"
                aria-label="Open menu"
                className={`lg:hidden inline-flex h-10 w-10 items-center justify-center ${solid ? "text-navy" : "text-white"}`}
              >
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-navy text-white border-none">
              <SheetHeader>
                <SheetTitle className="font-heading text-white text-left">
                  <img src="/logo-white.png" alt="BNZ Solutions" className="h-9 w-auto" width="160" height="87" />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.href}>
                    <a
                      href={l.href}
                      data-testid={`mobile-link-${l.label.toLowerCase()}`}
                      className="py-3 text-lg font-medium text-white/90 border-b border-white/10 transition-colors hover:text-gold"
                    >
                      {l.label}
                    </a>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <a
                    href="#contact"
                    data-testid="mobile-cta-quote"
                    className="mt-6 inline-flex items-center justify-center bg-gold px-5 py-3 text-sm font-semibold text-navy transition-colors hover:bg-gold-light"
                  >
                    Get a Quote
                  </a>
                </SheetClose>
                <a href={telLink} className="mt-3 inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 text-sm font-medium text-white">
                  <Phone className="h-4 w-4" /> {site.phone}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
